import { useEffect, useMemo, useState } from "react";
import type { PlanPreview, PlanRow } from "../../../shared/labels/contracts";
import type { LabelPlanSourceDetail, LabelPlanSourceListResponse } from "../../../shared/labels/work-order-request-contracts";
import { isSalesLabelCandidate, planExplanation, planLineName } from "../../../shared/labels/plan-visibility";
import type { LabelsApi } from "./labels-api";
import { uiFault } from "./labels-api";

export function selectedPlanSource(storageKey: string): string | null {
  try { return typeof window === "undefined" ? null : window.localStorage.getItem(storageKey); } catch { return null; }
}
export function useSalesPlanWorkspace(api: LabelsApi, enabled = true, demo = false, actorId = "") {
  const storageKey = "anl.labels.plan-source." + actorId;
  const [preview, setPreview] = useState<PlanPreview | null>(null);
  const [sources, setSources] = useState<LabelPlanSourceListResponse | null>(null);
  const [sourceId, setSourceId] = useState<string | null>(() => selectedPlanSource(storageKey));
  const [sourceDetail, setSourceDetail] = useState<LabelPlanSourceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [line, setLine] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  useEffect(() => { setSourceId(selectedPlanSource(storageKey)); setSelectedId(null); }, [storageKey]);
  useEffect(() => {
    const controller = new AbortController();
    if (!enabled) { setPreview(null); setSources(null); setSourceDetail(null); setLoading(false); return () => controller.abort(); }
    setLoading(true); setError(null); setPreview(null); setSourceDetail(null);
    void (async () => {
      if (demo) {
        const value = await api.demoSalesPlan(controller.signal);
        if (!controller.signal.aborted) setPreview(value);
        return;
      }
      const list = await api.planSources(controller.signal);
      if (controller.signal.aborted) return;
      setSources(list);
      // A remembered, unavailable source stays explicit instead of silently showing another week.
      const selected = sourceId ?? list.items[0]?.id;
      if (!selected) return;
      if (!sourceId) { setSourceId(selected); return; }
      const detail = await api.planSource(selected, controller.signal);
      if (!controller.signal.aborted) {
        setSourceDetail(detail);
        setPreview({ sourceId: detail.source.id, workbookName: detail.source.fileName, sha256: detail.source.sha256,
          importedAt: detail.source.importedAt, warnings: detail.source.warnings, rows: detail.rows.map(item => item.row), repeated: true });
      }
    })().catch((failure: unknown) => { if (!controller.signal.aborted) setError(uiFault(failure).message); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [api, reload, enabled, sourceId, demo]);
  const chooseSource = (id: string) => {
    setSourceId(id); setSelectedId(null); setLine("all"); setSearch(""); setPreview(null); setSourceDetail(null);
    try { window.localStorage.setItem(storageKey, id); } catch { /* Selection remains usable when storage is unavailable. */ }
  };
  const candidates = useMemo(() => preview?.rows.filter(isSalesLabelCandidate) ?? [], [preview]);
  const lines = useMemo(() => [...new Set(candidates.map(planLineName))].sort((a, b) => a.localeCompare(b, "tr")), [candidates]);
  const visible = useMemo(() => {
    const needle = search.toLocaleLowerCase("tr-TR").trim();
    return candidates.filter((row) => (line === "all" || planLineName(row) === line)
      && (!needle || [row.workOrder, row.customerName, row.productName, row.sku, planExplanation(row)]
        .some((value) => value?.toLocaleLowerCase("tr-TR").includes(needle))));
  }, [candidates, line, search]);
  const selected: PlanRow | null = visible.find((row) => row.id === selectedId) ?? null;
  return { preview, sources, sourceId, sourceDetail, chooseSource, loading, error, reload: () => setReload(value => value + 1), candidates, lines,
    line, setLine, search, setSearch, visible, selected, select: setSelectedId };
}
